import { ValidationItem } from '@app/queryTyping';

import { FieldInfoGroupField, HiddenField } from '@app/common/configurable-wizards';
import { SelectableGroupFieldFragment } from '@app/common/configurable-wizards/queries/queryTyping/all-fields';
import { isNotEmptyString, isString } from '@app/common/utils';

import { t } from '@app/widgets/contacts-and-settings/hooks/useUserSettingsTranslation';

import {
  AddressFormCache,
  AddressItemFields,
  AddressLines,
  AddressRecord,
  AddressTypes,
  AddressRecordFields,
} from '../types';

import { findField } from './subFieldsFinder';

export const getSelectedAddressField = (field: SelectableGroupFieldFragment) => field?.fieldGroups?.find(
  (item) => item?.id === field.selectedFieldGroupId,
) as FieldInfoGroupField;

export const isPhysicalAddressField = (field: SelectableGroupFieldFragment) => !!findField([field], (subField) => {
  const entity = subField as HiddenField;

  return entity.id === 'Type'
    && entity.sValue === AddressTypes[AddressTypes.Physical]
    ? subField
    : null;
});

/**
 * Extracts and filters address information from an AddressFormCache object.
 */
export const getProvidedAddress = ({
  [AddressItemFields.AddressGroup]: values,
}: AddressFormCache) => {
  const allowedFields = Object.values(AddressRecordFields);

  return allowedFields.reduce((filtered, field) => {
    if (values[field]) {
      return {
        ...filtered,
        [field]: values[field],
      };
    }

    return filtered;
  }, {} as AddressRecord);
};

/**
 * Converts an array of ValidationItem objects into a partial AddressRecord,
 * excluding invalid or missing fields.
 */
export const getSuggestedAddress = (values: ValidationItem[]) => {
  const result = {} as Partial<AddressRecord>;

  values.forEach(({ field, context }) => {
    if (field && context) {
      result[field as AddressRecordFields] = context;
    }
  });

  return result;
};

/**
 * Constructs an array of address lines from an AddressRecord object,
 * formatting them according to address conventions.
 */
export const getAddressLines = ({
  StreetAddress,
  StreetAddress2,
  City,
  State,
  ZIPCode,
}: AddressRecord) => {
  const city = City ? `${City}, ` : '';
  const state = State ? `${State} ` : '';

  return [
    StreetAddress || '',
    StreetAddress2 || '',
    `${city}${state}${ZIPCode || ''}`,
    t(
      'address-suggestion-dialog.address-line|Additional address line with default US country',
      'United States',
    ),
  ] as AddressLines;
};

/**
 * Converts an array of address lines into an HTML-formatted string.
 */
export const addressLinesToHTML = (address: AddressLines) => (
  address
    .filter((line) => isNotEmptyString(line))
    // eslint-disable-next-line i18next/no-literal-string
    .join('<br>')
);

/**
 * Convert the string values of an AddressRecord object to uppercase.
 */
export const addressRecordToUppercase = (address: AddressRecord) => (
  Object.keys(address).reduce((result, key) => {
    const value = address[key as keyof AddressRecord];

    return {
      ...result,
      [key]: isString(value) ? value.toUpperCase() : value,
    };
  }, {} as AddressRecord)
);

/**
 * Highlights differences between two arrays of address lines and returns an HTML-formatted string.
 *
 * @param {AddressLines} lines1 - An array of address lines, typically representing the original address.
 * @param {AddressLines} lines2 - An array of address lines, typically representing a modified or suggested address.
 * @returns {string} An HTML-formatted string highlighting the differences between the two address arrays.
 *
 * @example
 * const originalAddress = [
 *   '123 Main St',
 *   'Apt 456',
 *   'Example City, CA 12345',
 * ];
 *
 * const modifiedAddress = [
 *   '123 Main St',
 *   'Unit 456',
 *   'Example City, CA 12345',
 * ];
 *
 * const highlightedDiff = highlightAddressDiff(originalAddress, modifiedAddress);
 * // Output: '123 Main St<br><span>Apt</span><span>Unit</span> 456<br>Example City, CA 12345<br>'
 */
export const highlightAddressDiff = (lines1: AddressLines, lines2: AddressLines) => {
  let result = '';

  for (let i = 0; i < Math.max(lines1.length, lines2.length); i += 1) {
    const line1 = lines1[i];
    const line2 = lines2[i];

    if (line1 && line2) {
      const words1 = line1.split(/\s+/);
      const words2 = line2.split(/\s+/);

      let caretIndex = 0;

      for (let j = 0; j < words1.length; j += 1) {
        const word1 = words1[j] || '';
        const word2 = words2[j] || '';

        result += word1.toLowerCase() === word2.toLowerCase() ? word1 : `<span>${word1}</span>`;
        caretIndex += word1.length;

        while (
          caretIndex < line1.length
          && !/\S/.test(line1[caretIndex])
        ) {
          result += line1[caretIndex];
          caretIndex += 1;
        }
      }

      // eslint-disable-next-line i18next/no-literal-string
      result += '<br>';
    }
  }

  return result;
};
